<script lang="ts">
  // One dialog component, two presentations. At `sm` and up it is the
  // centred dialog it has always been. Below `sm` it becomes a bottom sheet:
  // pinned to the bottom edge, full width, rounded only at the top, with the
  // header and the optional footer held still while the body scrolls between
  // them. The DOM is identical in both — no branch, no second component, no
  // JS breakpoint to fall out of sync with CSS — so `role="dialog"`, the
  // Escape handler and the backdrop dismiss behave the same either way.
  import { onMount } from 'svelte';
  import { X } from 'lucide-svelte';
  import type { Snippet } from 'svelte';
  import { t } from '$lib/i18n';

  const {
    title,
    onClose,
    children,
    footer,
    wide = false,
    closable = true
  }: {
    title: string;
    onClose: () => void;
    children: Snippet;
    /** Held at the bottom edge of the sheet/dialog while the body scrolls —
        for the confirm/cancel pair of a long form, which otherwise sits off
        the bottom of a phone screen behind the keyboard. */
    footer?: Snippet;
    wide?: boolean;
    /** When false, hide the X, ignore Escape, and ignore backdrop clicks. */
    closable?: boolean;
  } = $props();

  function onKeydown(e: KeyboardEvent) {
    if (closable && e.key === 'Escape') onClose();
  }

  function onBackdropClick() {
    if (closable) onClose();
  }

  // Render on document.body so the overlay is not trapped in the app shell's
  // overflow/stacking context. Otherwise the mobile dock (z-30 sibling of
  // <main>) paints over the Punch in / Punch out actions.
  let overlay: HTMLDivElement | undefined;
  onMount(() => {
    const node = overlay;
    if (!node) return;
    document.body.appendChild(node);
    return () => node.remove();
  });
</script>

<svelte:window onkeydown={onKeydown} />

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  bind:this={overlay}
  class="fixed inset-0 z-50 flex items-end justify-center bg-[#0A1626]/50 backdrop-blur-[2px] sm:items-center sm:overflow-y-auto sm:px-4 sm:py-10"
  role="presentation"
  onclick={onBackdropClick}
>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div
    class="tym-modal-dialog tym-sheet w-full {wide
      ? 'sm:max-w-[720px]'
      : 'sm:max-w-[460px]'} rounded-[18px] bg-surface shadow-[0_24px_64px_rgba(0,0,0,.4)]"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    onclick={(e) => e.stopPropagation()}
  >
    <div class="tym-sheet-grabber sm:hidden" aria-hidden="true"></div>
    <div class="flex shrink-0 items-center justify-between p-5 pb-4 sm:p-[26px] sm:pb-4">
      <h2 class="font-heading text-lg font-bold text-navy">{title}</h2>
      {#if closable}
        <button
          type="button"
          onclick={onClose}
          class="tym-touch -mr-2 flex items-center justify-center rounded-lg px-2 py-2 text-dark-gray hover:text-navy"
          aria-label={t('common.close')}
        >
          <X class="h-5 w-5" />
        </button>
      {/if}
    </div>
    <div class="tym-modal-body px-5 pb-5 sm:px-[26px] sm:pb-[26px]">
      {@render children()}
    </div>
    {#if footer}
      <div class="tym-sheet-footer shrink-0 px-5 py-3 sm:px-[26px] sm:py-4">
        {@render footer()}
      </div>
    {/if}
  </div>
</div>
